import axios from 'axios';
import type { ApiResponse, LoginCredentials, User, MenuItem, OrdenItem } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Crear instancia de axios con caché
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar el token a las requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    if (error.response?.status === 403 && error.response?.data?.error === 'Token inválido') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<ApiResponse<{ user: User; token: string }>> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  verify: async (token: string): Promise<ApiResponse<User>> => {
    const response = await api.post('/auth/verify', { token });
    return response.data;
  },
};

// Menu API
export const menuAPI = {
  getDashboardData: async (): Promise<ApiResponse<{ menuItems: MenuItem[] }>> => {
    const response = await api.get('/menu/dashboard');
    return response.data;
  },

  getItems: async (queryParams?: string): Promise<ApiResponse<MenuItem[]>> => {
    const url = queryParams ? `/menu/items?${queryParams}` : '/menu/items';
    const response = await api.get(url);
    return response.data;
  },
  
  getItem: async (id: string): Promise<ApiResponse<MenuItem>> => {
    const response = await api.get(`/menu/items/${id}`);
    return response.data;
  },
};

// Añadir estos métodos al objeto exportado
export const orderAPI = {
  createOrder: async (orderData: { items: OrdenItem[], total: number }): Promise<ApiResponse<any>> => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },
  
  getOrders: async (queryParams?: string): Promise<ApiResponse<any[]>> => {
    const url = queryParams ? `/orders?${queryParams}` : '/orders';
    const response = await api.get(url);
    return response.data;
  },
  
  getOrder: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  },
  
  deleteOrder: async (id: string): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/orders/${id}`);
    return response.data;
  },
  
  deleteMultipleOrders: async (orderIds: string[]): Promise<ApiResponse<any>> => {
    const response = await api.delete('/orders', { data: { orderIds } });
    return response.data;
  }
};

// Analytics API
export const analyticsAPI = {
  getSummary: async (year?: number, month?: number): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const response = await api.get(`/analytics/summary?${params.toString()}`);
    return response.data;
  },

  getMonthlySales: async (year?: number): Promise<ApiResponse<any[]>> => {
    const url = year ? `/analytics/monthly?year=${year}` : '/analytics/monthly';
    const response = await api.get(url);
    return response.data;
  },

  getDailySales: async (year?: number, month?: number): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const response = await api.get(`/analytics/daily?${params.toString()}`);
    return response.data;
  },

  getByTurn: async (year?: number, month?: number): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const response = await api.get(`/analytics/by-turn?${params.toString()}`);
    return response.data;
  },

  getTopProducts: async (limit: number = 10, year?: number, month?: number): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    
    const response = await api.get(`/analytics/top-products?${params.toString()}`);
    return response.data;
  },

  getCorrelations: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/analytics/correlations');
    return response.data;
  }
};

// Asegúrate de exportar orderAPI junto con menuAPI
export default api;